import React, { memo, useCallback, useEffect, useRef, type FC } from 'react';
import { Platform, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';

import type { AvailableIconName } from '@/assets/icons';
import { PressableSurface } from '@/components/ui';
import { useAppleLogin } from '@/features/auth/hooks/use-apple-login';
import { useGoogleLogin } from '@/features/auth/hooks/use-google-login';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

export type SocialProvider = 'facebook' | 'google' | 'apple';

type ProviderConfig = {
  icon: AvailableIconName;
  iconSize: number;
  iconColor?: string;
  accessibilityLabel: string;
  text?: string;
};

const PROVIDERS: Record<SocialProvider, ProviderConfig> = {
  facebook: {
    icon: 'IcFacebook',
    iconSize: 24,
    accessibilityLabel: 'Continue with Facebook',
    text: 'Facebook',
  },
  google: {
    icon: 'IcGoogle',
    iconSize: 20,
    accessibilityLabel: 'Continue with Google',
    text: 'Google',
  },
  apple: {
    icon: 'IcApple',
    iconSize: 20,
    iconColor: colors['10'],
    accessibilityLabel: 'Continue with Apple',
    text: 'Apple',
  },
};

export type SocialAuthButtonProps = {
  provider: SocialProvider;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  afterSignIn?: () => void;
};

/**
 * SocialAuthButton – white pill button (80×48) with a brand icon, used in the
 * "or login with" row. Built on the shared `PressableSurface`.
 */
const SocialAuthButtonComponent: FC<SocialAuthButtonProps> = ({
  provider,
  disabled,
  style,
  afterSignIn,
}) => {
  const { icon, iconSize, iconColor, accessibilityLabel, text } = PROVIDERS[provider];
  const { isTablet } = useScaling();
  const { handleAppleLogin, handleAppleLoginAndroid } = useAppleLogin();
  const { handleGoogleLogin } = useGoogleLogin();

  const afterSignInRef = useRef(afterSignIn);

  useEffect(() => {
    afterSignInRef.current = afterSignIn;
  }, [afterSignIn]);

  const onPressIos = useCallback(async () => {
    if (provider === 'apple') {
      await handleAppleLogin();
    } else if (provider === 'google') {
      await handleGoogleLogin();
    }
    afterSignInRef?.current?.();
  }, [handleAppleLogin, handleGoogleLogin, provider]);

  const onPressAndroid = useCallback(async () => {
    if (provider === 'apple') {
      await handleAppleLoginAndroid();
    } else if (provider === 'google') {
      await handleGoogleLogin();
    }
    afterSignInRef?.current?.();
  }, [handleAppleLoginAndroid, handleGoogleLogin, provider]);

  return (
    <PressableSurface
      onPress={Platform.select({
        android: onPressAndroid,
        ios: onPressIos,
        // Web goes through the same Firebase popup flow as iOS (no native module).
        default: onPressIos,
      })}
      disabled={disabled}
      width={isTablet ? 160 : 120}
      height={48}
      borderRadius={28}
      backgroundColor={colors['0']}
      accessibilityLabel={accessibilityLabel}
      style={[styles.shadow, style]}>
      <PressableSurface.Icon name={icon} size={iconSize} color={iconColor} />
      {text ? (
        <PressableSurface.Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold">
          {text}
        </PressableSurface.Text>
      ) : null}
    </PressableSurface>
  );
};

const SHADOW_COLOR = '#795465';

const styles = StyleSheet.create({
  shadow: Platform.select({
    ios: {
      shadowColor: SHADOW_COLOR,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 2,
    },
    default: {
      elevation: 1,
    },
  }),
});

export const SocialAuthButton = memo(SocialAuthButtonComponent);

export default SocialAuthButton;
